import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useFeedBack } from "../../../context/FeedBackContext";
import excelTemplate from "../../../assets/excels/plantilla para importar valores de cuentas de año de planificacion.xlsx";
import update from 'immutability-helper';
import { createAxios } from "../../../helpers/createAxios";
import { ProgressBar, Spinner } from "react-bootstrap";
import { FiCheckCircle, FiAlertTriangle, FiUploadCloud, FiFileText } from "react-icons/fi";
import swal from "sweetalert";

const ImportPlanAccountsValues = () => {
    const { setCustomAlert } = useFeedBack();

    const [data, setData] = useState({
        file: null,
        emails: [''],
    });

    const [importState, setImportState] = useState({
        isImporting: false,
        isFinished: false,
        totalRows: 0,
        processedRows: 0,
        progressPercent: 0,
        currentChunk: 0,
        totalChunks: 0,
        updatedCount: 0,
        statusMessage: '',
        errors: [],
    });

    const [showInvalidRows, setShowInvalidRows] = useState(true);

    const handleSubmit = async (e) => {
        e?.preventDefault?.();

        if (importState.isImporting) return;

        if (!data?.file) {
            swal({
                title: "Archivo requerido",
                text: "Por favor seleccione un archivo Excel para importar.",
                icon: "warning",
            });
            return;
        }

        const validEmails = data?.emails.filter(email => email.trim().length > 0);
        if (validEmails.length === 0) {
            swal({
                title: "Correo requerido",
                text: "Ingrese al menos un correo electrónico válido para recibir la notificación.",
                icon: "warning",
            });
            return;
        }

        const axiosClient = createAxios();

        setImportState({
            isImporting: true,
            isFinished: false,
            totalRows: 0,
            processedRows: 0,
            progressPercent: 0,
            currentChunk: 0,
            totalChunks: 0,
            updatedCount: 0,
            statusMessage: 'Leyendo archivo Excel y precargando información...',
            errors: [],
        });

        try {
            // Paso 1: Subida inicial y precarga
            const formData = new FormData();
            formData.append('file', data.file);
            validEmails.forEach((email, i) => {
                formData.append(`emails[${i}]`, email);
            });

            const initRes = await axiosClient.post('/plan-accounts/import/init', formData);
            const { import_id, total_rows, chunk_size, total_chunks } = initRes.data;

            if (!total_rows || total_rows === 0) {
                setImportState(prev => ({
                    ...prev,
                    isImporting: false,
                    isFinished: true,
                    statusMessage: 'El archivo no contiene filas de datos para importar.',
                }));
                return;
            }

            setImportState(prev => ({
                ...prev,
                totalRows: total_rows,
                totalChunks: total_chunks,
                statusMessage: `Iniciando procesamiento de ${total_rows} filas en ${total_chunks} lotes...`,
            }));

            let accumulatedErrors = [];
            let totalUpdated = 0;

            // Paso 2: Procesamiento por lotes con actualización de progreso en tiempo real
            for (let chunkIndex = 0; chunkIndex < total_chunks; chunkIndex++) {
                const chunkRes = await axiosClient.post('/plan-accounts/import/chunk', {
                    import_id,
                    chunk_index: chunkIndex,
                    chunk_size,
                });

                const chunkData = chunkRes.data;
                const chunkUpdated = chunkData.processed_in_chunk || 0;
                const chunkErrors = chunkData.errors || [];
                totalUpdated += chunkUpdated;
                accumulatedErrors = accumulatedErrors.concat(chunkErrors);

                const currentSnapshotUpdated = totalUpdated;
                const currentSnapshotErrors = accumulatedErrors;

                setImportState(prev => Object.assign({}, prev, {
                    processedRows: chunkData.current_row,
                    progressPercent: chunkData.percent,
                    currentChunk: chunkIndex + 1,
                    updatedCount: currentSnapshotUpdated,
                    statusMessage: `Cargando lote ${chunkIndex + 1} de ${total_chunks} (${chunkData.current_row} de ${total_rows} filas)...`,
                    errors: currentSnapshotErrors,
                }));
            }

            // Paso 3: Finalización exitosa
            setImportState(prev => ({
                ...prev,
                isImporting: false,
                isFinished: true,
                processedRows: total_rows,
                progressPercent: 100,
                statusMessage: '¡Proceso de importación finalizado con éxito!',
                errors: accumulatedErrors,
            }));

            setCustomAlert({
                show: true,
                message: `El archivo se ha importado exitosamente. Se procesaron ${total_rows} filas.`,
                severity: "success",
                title: 'Operación exitosa',
            });

            swal({
                title: "¡Importación Exitosa!",
                text: `Se procesaron ${total_rows} filas correctamente en pocos segundos. Se ha enviado un correo a los destinatarios.`,
                icon: "success",
            });

        } catch (error) {
            const errorMsg = error?.response?.data?.message || error?.message || 'Ocurrió un error al procesar la importación.';
            setImportState(prev => ({
                ...prev,
                isImporting: false,
                statusMessage: `Error: ${errorMsg}`,
            }));

            swal({
                title: "Error en la importación",
                text: errorMsg,
                icon: "error",
            });
        }
    };

    const handleChange = (e) => {
        const file = e.target.files?.[0] || null;
        setData(oldData => ({
            ...oldData,
            file
        }));
    };

    const handleEmailChange = (e, i) => {
        const newEmailsValues = update(data?.emails, { [i]: { $set: e.target.value } });
        setData(oldData => ({
            ...oldData,
            emails: newEmailsValues
        }));
    };

    const handleRemoveEmail = (i) => {
        const newEmailsValues = update(data?.emails, { $splice: [[i, 1]] });
        setData(oldData => ({
            ...oldData,
            emails: newEmailsValues
        }));
    };

    return (
        <div className="card shadow-sm border-0" style={{ borderRadius: "12px" }}>
            <div className="card-header bg-white border-bottom py-3">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                    <div>
                        <h4 className="fw-bold mb-1" style={{ color: "#0f172a" }}>
                            Importar montos y gastos de planificación
                        </h4>
                        <small className="text-muted">
                            Carga masiva de valores y gastos reales con procesamiento ultrarrápido y seguimiento en vivo
                        </small>
                    </div>
                    <div>
                        <a
                            href={excelTemplate}
                            download
                            className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
                            style={{ borderRadius: "6px" }}
                        >
                            <FiFileText size={15} />
                            Descargar plantilla Excel
                        </a>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="card-body p-4">
                    {/* Panel de Progreso en Tiempo Real */}
                    {(importState.isImporting || importState.isFinished) && (
                        <div
                            className="card border-0 mb-4 shadow-sm"
                            style={{
                                borderRadius: "10px",
                                backgroundColor: importState.isFinished ? "#f8fafc" : "#f0fdf4",
                                border: importState.isFinished ? "1px solid #e2e8f0" : "1px solid #bbf7d0",
                            }}
                        >
                            <div className="card-body p-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <div className="d-flex align-items-center gap-2">
                                        {importState.isImporting && (
                                            <Spinner animation="border" size="sm" variant="primary" />
                                        )}
                                        {importState.isFinished && (
                                            <FiCheckCircle size={20} className="text-success" />
                                        )}
                                        <span className="fw-semibold" style={{ fontSize: "0.95rem", color: "#0f172a" }}>
                                            {importState.statusMessage}
                                        </span>
                                    </div>
                                    <div className="fw-bold" style={{ fontSize: "1.1rem", color: "#0f172a" }}>
                                        {importState.processedRows} de {importState.totalRows} filas cargadas ({importState.progressPercent}%)
                                    </div>
                                </div>

                                <ProgressBar
                                    animated={importState.isImporting}
                                    striped
                                    variant={importState.isFinished ? "success" : "primary"}
                                    now={importState.progressPercent}
                                    style={{ height: "22px", borderRadius: "8px", fontWeight: 600, fontSize: "0.85rem" }}
                                    label={`${importState.progressPercent}%`}
                                />

                                <div className="d-flex justify-content-between align-items-center mt-2 text-muted" style={{ fontSize: "0.8rem" }}>
                                    <span>
                                        {importState.totalChunks > 0 && (
                                            <>Lote {importState.currentChunk} de {importState.totalChunks}</>
                                        )}
                                    </span>
                                    <div className="d-flex gap-2">
                                        {importState.errors.length > 0 && (
                                            <span className="badge bg-danger">
                                                {importState.errors.length} filas con advertencias
                                            </span>
                                        )}
                                        {importState.isFinished && (
                                            <span className="badge bg-success">
                                                Completado al 100%
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Selector de Archivo Excel */}
                    <div className="mb-4">
                        <label className="form-label fw-semibold text-primary mb-1">
                            Archivo Excel (.xlsx o .xls) <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                            <input
                                type="file"
                                name="file"
                                className="form-control"
                                accept=".xlsx, .xls"
                                onChange={handleChange}
                                disabled={importState.isImporting}
                                style={{ borderRadius: "6px" }}
                            />
                        </div>
                        {data.file && (
                            <small className="text-muted d-flex align-items-center gap-1 mt-1">
                                <FiUploadCloud /> Archivo seleccionado: <strong>{data.file.name}</strong> ({(data.file.size / 1024).toFixed(1)} KB)
                            </small>
                        )}
                    </div>

                    {/* Notificaciones por Correo Electrónico */}
                    <div className="mb-3">
                        <h6 className="fw-semibold mb-2" style={{ color: "#334155" }}>
                            Correos a notificar al finalizar el proceso <span className="text-danger">*</span>
                        </h6>
                        <small className="text-muted d-block mb-3">
                            Se enviará un resumen de finalización a los siguientes correos.
                        </small>

                        {data?.emails?.map((email, i) => (
                            <div className="input-group mb-2" key={i} style={{ maxWidth: "550px" }}>
                                {data.emails.length > 1 && (
                                    <button
                                        className="btn btn-outline-danger"
                                        type="button"
                                        disabled={importState.isImporting}
                                        onClick={() => handleRemoveEmail(i)}
                                    >
                                        Eliminar
                                    </button>
                                )}
                                <input
                                    type="email"
                                    onChange={(e) => handleEmailChange(e, i)}
                                    className="form-control"
                                    placeholder="ejemplo@csiltda.cl"
                                    value={email}
                                    disabled={importState.isImporting}
                                    style={{ borderRadius: data.emails.length > 1 ? "0 6px 6px 0" : "6px" }}
                                />
                            </div>
                        ))}

                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm mt-1"
                            disabled={importState.isImporting}
                            onClick={() => setData(oldData => ({ ...oldData, emails: [...oldData.emails, ''] }))}
                            style={{ borderRadius: "6px" }}
                        >
                            + Agregar otro correo
                        </button>
                    </div>

                    {/* Detalle de Filas con Errores / Advertencias */}
                    {importState.errors.length > 0 && showInvalidRows && (
                        <div className="mt-4 p-3 border rounded" style={{ backgroundColor: "#fef2f2", borderColor: "#fecaca" }}>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <h6 className="text-danger fw-bold mb-0 d-flex align-items-center gap-1">
                                    <FiAlertTriangle /> Filas con observaciones o errores: {importState.errors.length}
                                </h6>
                                <button
                                    className="btn btn-outline-danger btn-sm"
                                    type="button"
                                    onClick={() => setShowInvalidRows(false)}
                                >
                                    Ocultar observaciones
                                </button>
                            </div>
                            <div style={{ maxHeight: "300px", overflowY: "auto", fontSize: "0.82rem" }}>
                                {importState.errors.map((error, i) => (
                                    <div key={i} className="mb-2 pb-1 border-bottom">
                                        <div className="fw-semibold text-danger">
                                            Fila {error.row}:
                                        </div>
                                        <ul className="mb-1 ps-3 text-muted">
                                            {error.errors?.map((errDesc, i2) => (
                                                <li key={i2}>{errDesc}</li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="card-footer bg-light d-flex justify-content-end gap-2 py-3">
                    <Link
                        to={'/centros-de-costos/listar'}
                        className="btn btn-secondary btn-sm"
                        style={{ borderRadius: "6px" }}
                    >
                        Volver
                    </Link>

                    <button
                        type="submit"
                        disabled={!data?.file || importState.isImporting}
                        className="btn btn-primary btn-sm d-flex align-items-center gap-1"
                        style={{ borderRadius: "6px", fontWeight: 600 }}
                    >
                        {importState.isImporting ? (
                            <>
                                <Spinner animation="border" size="sm" />
                                <span>Cargando ({importState.progressPercent}%)...</span>
                            </>
                        ) : (
                            <>
                                <FiUploadCloud size={16} />
                                <span>Iniciar Importación</span>
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ImportPlanAccountsValues;